import { useEffect, useState, useRef } from "react";
import { useNavigate, Link, useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import * as Icons from "lucide-react";
import { Search, ArrowRight, Calendar, BookOpen, Eye, Download, Quote } from "lucide-react";

const DEFAULT_CAROUSEL = [
  { src: "https://static.prod-images.emergentagent.com/jobs/9905877b-e61e-4994-b128-221fdaccc98f/images/5878d61141a80e85340a5d6fd044be80d4b3e3b5ac279fdb4e38d5fe0360ceb0.jpeg", label: "Medicine & Life Sciences" },
  { src: "https://static.prod-images.emergentagent.com/jobs/9905877b-e61e-4994-b128-221fdaccc98f/images/b7d347b956bbac56c33bf480bf3b43175f77796f950cd019ac85fe09c86b113b.jpeg", label: "Physics & Mathematics" },
  { src: "https://static.prod-images.emergentagent.com/jobs/9905877b-e61e-4994-b128-221fdaccc98f/images/869b5beb7a95637e738a852d22adbc4e169b6fe1368eee63348196e7bf52f630.jpeg", label: "Chemistry & Materials" },
  { src: "https://static.prod-images.emergentagent.com/jobs/9905877b-e61e-4994-b128-221fdaccc98f/images/512f5cccd4bf9ec80da7c7faf025205cb8c4d4e0c4931da1f143edac3427ba80.jpeg", label: "Computer Science" },
  { src: "https://static.prod-images.emergentagent.com/jobs/9905877b-e61e-4994-b128-221fdaccc98f/images/a622f8e6b8402f0603a504cb7dc5c023a0f795d3a0ae3dfc91929213572ee6b6.jpeg", label: "Earth & Environment" },
];

function CategoryIcon({ name, className }) {
  const Ico = Icons[name] || BookOpen;
  return <Ico className={className} />;
}

function HeroCarousel({ slides }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setI((p) => (p + 1) % slides.length), 3500);
    return () => clearInterval(t);
  }, [slides.length]);
  if (!slides.length) return null;
  const active = Math.min(i, slides.length - 1);
  return (
    <div className="relative w-full max-w-[240px] mx-auto" data-testid="hero-carousel">
      <div className="absolute -top-5 -right-5 w-24 h-24 rounded-full bg-amber-500/30 blur-2xl" />
      <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/20 aspect-square">
        {slides.map((s, idx) => (
          <img
            key={s.src}
            src={s.src}
            alt={s.label}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
            style={{ opacity: idx === active ? 1 : 0 }}
          />
        ))}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-primary/90 to-transparent p-4 pt-10">
          <p className="text-xs font-mono uppercase tracking-widest text-amber-300">{slides[active].label}</p>
        </div>
      </div>
      <div className="flex justify-center gap-1.5 mt-3">
        {slides.map((_, idx) => (
          <button key={idx} onClick={() => setI(idx)} aria-label={`Slide ${idx + 1}`}
            className={`h-1.5 rounded-full transition-all ${idx === active ? "w-5 bg-amber-400" : "w-1.5 bg-white/30"}`} />
        ))}
      </div>
    </div>
  );
}

function RecentRow({ paper }) {
  const navigate = useNavigate();
  const year = paper.published_at ? new Date(paper.published_at).getFullYear() : null;
  let meta = "";
  if (paper.volume) meta += `Vol. ${paper.volume}`;
  if (year) meta += `${meta ? " " : ""}(${year})`;
  if (paper.pages) meta += `${meta ? ", " : ""}pp. ${paper.pages}`;
  return (
    <article
      data-testid={`paper-card-${paper.id}`}
      onClick={() => navigate(`/paper/${paper.id}`)}
      className="group cursor-pointer py-5 px-3 -mx-3 rounded-lg hover:bg-white dark:hover:bg-slate-900/50 transition-colors duration-200"
    >
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 text-[11px]">{paper.article_type}</Badge>
        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 text-[11px]">Open Access</Badge>
        {paper.published_at && (
          <span className="inline-flex items-center gap-1 text-sm font-bold text-primary dark:text-sky-400 ml-auto"><Calendar className="w-3.5 h-3.5" /> {new Date(paper.published_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</span>
        )}
      </div>
      <h3 className="font-serif font-bold text-lg leading-snug text-slate-900 dark:text-slate-100 group-hover:text-primary dark:group-hover:text-sky-300 transition-colors">{paper.title}</h3>
      <p className="text-sm font-bold text-primary dark:text-sky-400 mt-1">{paper.authors?.map((a) => a.name).join(", ")}</p>
      <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 leading-relaxed line-clamp-3 text-justify" data-testid="recent-abstract">{paper.abstract}</p>
      <div className="mt-2.5 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap text-sm">
          {paper.journal && <Link onClick={(e) => e.stopPropagation()} to={`/journal/${paper.journal.slug}`} className="font-bold text-primary dark:text-sky-400 hover:underline">{paper.journal.title}</Link>}
          {meta && <span className="text-slate-500 dark:text-slate-400">· {meta}</span>}
          {paper.doi && <span className="font-mono text-xs text-slate-400 hidden sm:inline">· DOI: {paper.doi}</span>}
        </div>
        <div className="flex items-center gap-4 text-xs font-bold text-emerald-600 dark:text-emerald-400">
          <span className="inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {paper.views || 0}</span>
          <span className="inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" /> {paper.downloads || 0}</span>
          <span className="inline-flex items-center gap-1"><Quote className="w-3.5 h-3.5" /> {paper.citations || 0}</span>
        </div>
      </div>
    </article>
  );
}

function SpecialCard({ s }) {
  return (
    <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden hover:shadow-md transition-all h-full flex flex-col">
      <div className="h-28 w-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
        {s.journal?.cover_image && <img src={s.journal.cover_image} alt={s.journal.title} className="w-full h-full object-cover" loading="lazy" />}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 text-xs text-amber-600 font-medium mb-2">
          <Calendar className="w-4 h-4" /> Due {s.due_date}
        </div>
        <h3 className="font-serif font-bold text-lg text-slate-900 dark:text-slate-100 leading-snug">{s.title}</h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 line-clamp-2 flex-1">{s.description}</p>
        {s.journal && <Link to={`/journal/${s.journal.slug}`} className="mt-3 inline-block text-sm font-bold text-primary dark:text-sky-400 hover:underline">{s.journal.title}</Link>}
      </div>
    </div>
  );
}

function SpecialsCarousel({ specials }) {
  const [idx, setIdx] = useState(0);
  const [step, setStep] = useState(0);
  const [anim, setAnim] = useState(true);
  const trackRef = useRef(null);
  const n = specials.length;
  const list = n ? [...specials, ...specials] : [];

  useEffect(() => {
    const measure = () => { const el = trackRef.current?.children?.[0]; if (el) setStep(el.getBoundingClientRect().width); };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [n]);

  useEffect(() => {
    if (n <= 1) return;
    const t = setInterval(() => setIdx((i) => i + 1), 3000);
    return () => clearInterval(t);
  }, [n]);

  useEffect(() => {
    if (idx === n && n > 0) {
      const t = setTimeout(() => { setAnim(false); setIdx(0); }, 650);
      return () => clearTimeout(t);
    }
    if (!anim) { const r = requestAnimationFrame(() => setAnim(true)); return () => cancelAnimationFrame(r); }
  }, [idx, n, anim]);

  if (!n) return <p className="text-slate-500 text-sm">No open special issues right now.</p>;
  return (
    <div className="overflow-hidden" data-testid="specials-carousel">
      <div ref={trackRef} className="flex" style={{ transform: `translateX(-${idx * step}px)`, transition: anim ? "transform 0.6s ease" : "none" }}>
        {list.map((s, i) => (
          <div key={i} className="shrink-0 basis-full sm:basis-1/2 lg:basis-1/3 pr-5">
            <SpecialCard s={s} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  const { settings } = useOutletContext();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [cats, setCats] = useState([]);
  const [recent, setRecent] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [home, setHome] = useState(null);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
    api.get("/papers/recent?limit=10").then(({ data }) => setRecent(data)).catch(() => {});
    api.get("/special-issues").then(({ data }) => setSpecials(data)).catch(() => {});
    api.get("/home-config").then(({ data }) => setHome(data)).catch(() => {});
  }, []);

  const slides = (home?.carousel?.length ? home.carousel : DEFAULT_CAROUSEL).filter((s) => s.enabled !== false);
  const shownCats = home?.disciplines?.length
    ? home.disciplines.map((s) => cats.find((c) => c.slug === s)).filter(Boolean)
    : cats.slice(0, 6);

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div>
      {/* Compact hero */}
      <section className="relative overflow-hidden bg-primary text-white">
        <div className="absolute inset-0 hero-grid opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 py-6 md:py-7 grid lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
          <div className="animate-fade-up">
            <Badge className="bg-white/10 text-amber-300 border-0 mb-3 font-mono text-[11px] tracking-widest uppercase">
              {settings?.tagline || "Open Access · Peer Reviewed"}
            </Badge>
            <h1 className="font-serif font-black text-3xl md:text-4xl leading-[1.1] tracking-tight">
              {settings?.hero_title || "Advancing Knowledge Through Open Access"}
            </h1>
            <p className="mt-3 text-base text-slate-300 max-w-xl leading-relaxed">
              {settings?.hero_subtitle || "Discover peer-reviewed research across every discipline. Read, download and cite freely."}
            </p>

            <form onSubmit={doSearch} className="mt-5 flex flex-col sm:flex-row gap-3 max-w-lg">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  data-testid="hero-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search articles, keywords, authors…"
                  className="pl-12 h-11 text-base rounded-full bg-white text-slate-900 border-0 shadow-lg"
                />
              </div>
              <Button type="submit" data-testid="hero-search-btn" className="h-11 px-6 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-semibold">
                Search <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </form>

            <div className="mt-4 flex flex-wrap gap-3">
              <Button asChild variant="outline" size="sm" className="rounded-full border-white/30 bg-white/5 text-white hover:bg-white/15" data-testid="hero-browse-articles">
                <Link to="/articles">Browse Articles</Link>
              </Button>
              <Button asChild variant="ghost" size="sm" className="rounded-full text-slate-200 hover:text-white hover:bg-white/10" data-testid="hero-submit-paper">
                <Link to="/for-authors">For Authors <ArrowRight className="w-4 h-4 ml-1" /></Link>
              </Button>
            </div>
          </div>

          <div className="hidden lg:block animate-fade-up" style={{ animationDelay: "120ms" }}>
            <HeroCarousel slides={slides} />
          </div>
        </div>
      </section>

      {/* Categories — first 6 */}
      <section id="categories" className="max-w-7xl mx-auto px-6 pt-10 pb-6 scroll-mt-24">
        <div className="flex items-end justify-between mb-5">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-amber-600 font-semibold mb-1.5">Browse by Subject</p>
            <h2 className="font-serif font-bold text-2xl sm:text-3xl text-slate-900 dark:text-slate-100">Journals by Discipline</h2>
          </div>
          <Button asChild variant="outline" size="sm" className="hidden sm:flex rounded-full" data-testid="view-all-journals">
            <Link to="/journals">All Journals <ArrowRight className="w-4 h-4 ml-1" /></Link>
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {shownCats.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              data-testid={`category-card-${c.slug}`}
              className="group flex items-center gap-3 bg-card rounded-lg border border-slate-200 dark:border-slate-800 p-3.5 hover:shadow-sm hover:border-primary/30 transition-all duration-200"
            >
              <span className="grid place-items-center w-10 h-10 rounded-lg bg-primary/5 dark:bg-primary/20 text-primary dark:text-sky-300 group-hover:bg-primary group-hover:text-white transition-colors shrink-0">
                <CategoryIcon name={c.icon} className="w-5 h-5" />
              </span>
              <div className="min-w-0">
                <h3 className="font-serif font-bold text-base text-slate-900 dark:text-slate-100 group-hover:text-primary dark:group-hover:text-sky-300 transition-colors leading-tight line-clamp-1">{c.name}</h3>
                {c.journal && <p className="text-xs text-slate-500 italic mt-0.5 line-clamp-1">{c.journal.title}</p>}
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Recently Published — full-width rows separated by thin lines */}
      <section className="bg-slate-50 dark:bg-slate-900/40 border-y border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-amber-600 font-semibold mb-1.5">Latest Research</p>
              <h2 className="font-serif font-bold text-2xl sm:text-3xl text-slate-900 dark:text-slate-100">Recently Published</h2>
            </div>
            <Button asChild variant="outline" size="sm" className="hidden sm:flex rounded-full" data-testid="view-all-articles">
              <Link to="/articles">All Articles <ArrowRight className="w-4 h-4 ml-1" /></Link>
            </Button>
          </div>
          <div className="divide-y divide-slate-200 dark:divide-slate-800 border-t border-slate-200 dark:border-slate-800">
            {recent.length === 0 && <p className="py-8 text-sm text-slate-500" data-testid="recent-empty">No articles have been published yet. New articles will appear here as soon as they are published.</p>}
            {recent.map((p) => <RecentRow key={p.id} paper={p} />)}
          </div>
        </div>
      </section>

      {/* Special Issues */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-5">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-amber-600 font-semibold mb-1.5">Call for Papers</p>
          <h2 className="font-serif font-bold text-2xl sm:text-3xl text-slate-900 dark:text-slate-100">Open Special Issues</h2>
        </div>
        <SpecialsCarousel specials={specials} />
      </section>
    </div>
  );
}
